import {
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
  type MetaFunction,
  redirect,
} from "react-router";
import {
  Form,
  useLoaderData,
  useActionData,
  useNavigation,
  Link,
} from "react-router";
import { getSession, commitSession } from "~/session.server";
import {
  getMsUser,
  createPendingSignup,
  validatePasswordStrength,
} from "~/utils/ms-auth.server";
import { validatePromoCode } from "~/models/promo-code.server";
import {
  verifyTurnstileWithCache,
  getTurnstileSiteKey,
  isTurnstileChallengeRequired,
} from "~/utils/turnstile.server";
import { TurnstileWidget } from "~/components/auth/TurnstileWidget";
import { sendVerificationCodeEmail } from "~/utils/email-templates.server";
import { trackSignupAnalytics } from "~/utils/signup-analytics.server";
import {
  isBusinessEmail,
  getSignupEmailDomain,
  BUSINESS_EMAIL_ERROR,
} from "~/utils/business-email.server";
import { logger } from "~/utils/logger.server";
import { prisma as ms_prisma } from "~/mediascribe-db.server";
import { Button } from "~/components/button/Button";
import { Alert } from "~/components/alert/Alert";
import { FormInput } from "~/components/form-input";

export const meta: MetaFunction = () => [
  { title: "MediaScribe - Create Account" },
];

interface LoaderData {
  turnstileSiteKey: string | undefined;
  turnstileRequired: boolean;
  inviteCodeRequired: boolean;
}

export const loader = async ({
  request,
}: LoaderFunctionArgs): Promise<LoaderData | Response> => {
  // Check if user is already logged in
  const user = await getMsUser(request);
  if (user) {
    return redirect("/projects");
  }

  // Get session to capture signup context from URL params and check Turnstile cache
  const session = await getSession(request.headers.get("cookie"));
  const url = new URL(request.url);

  const affiliate = url.searchParams.get("affiliate");
  const promo = url.searchParams.get("promo");

  if (affiliate) {
    session.set("signupAffiliate", affiliate);
  }
  if (promo) {
    session.set("signupPromo", promo);
  }

  // Check if Turnstile challenge is required (uses 25 min threshold)
  const turnstileRequired = isTurnstileChallengeRequired(session, request);

  return {
    turnstileSiteKey: getTurnstileSiteKey(),
    turnstileRequired,
    inviteCodeRequired: !!process.env.INVITE_CODE,
  };
};

interface ActionData {
  error?: string;
  fieldErrors?: {
    email?: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    organizationName?: string;
  };
}

export const action = async ({
  request,
}: ActionFunctionArgs): Promise<ActionData | Response> => {
  const formData = await request.formData();
  const email = formData.get("email") as string;
  const password = formData.get("password") as string;
  const confirmPassword = formData.get("confirmPassword") as string;
  const firstName = formData.get("firstName") as string;
  const lastName = formData.get("lastName") as string;
  const organizationName = formData.get("organizationName") as string;

  // Validate inputs
  const fieldErrors: ActionData["fieldErrors"] = {};

  // Require a well-formed shape (exactly one "@", a dotted domain) so malformed
  // input gets this format error rather than falling through to the freemail
  // block below, which would return a misleading "use your business email".
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    fieldErrors.email = "Please enter a valid email address.";
  }

  if (!firstName || firstName.trim().length === 0) {
    fieldErrors.firstName = "First name is required.";
  }

  if (!lastName || lastName.trim().length === 0) {
    fieldErrors.lastName = "Last name is required.";
  }

  if (!organizationName || organizationName.trim().length === 0) {
    fieldErrors.organizationName = "Organization name is required.";
  }

  // Validate password strength
  const passwordValidation = validatePasswordStrength(password || "");
  if (!passwordValidation.isValid) {
    fieldErrors.password = passwordValidation.errors[0]; // Show first error
  }

  if (passwordValidation.isValid && password !== confirmPassword) {
    fieldErrors.password = "Passwords do not match.";
  }

  if (Object.keys(fieldErrors).length > 0) {
    return { fieldErrors };
  }

  // Validate invite code if required (staging environments)
  if (process.env.INVITE_CODE) {
    const inviteCode = formData.get("inviteCode");
    if (
      typeof inviteCode !== "string" ||
      inviteCode.trim() !== process.env.INVITE_CODE
    ) {
      return { error: "Invalid invite code." };
    }
  }

  // Get session for Turnstile caching and signup context
  const session = await getSession(request.headers.get("cookie"));

  // Verify Turnstile (uses session cache if available)
  const turnstileResult = await verifyTurnstileWithCache(
    formData,
    request,
    session,
  );
  if (!turnstileResult.success) {
    return { error: turnstileResult.error || "Bot verification failed." };
  }

  // Block free/personal email domains (gmail, yahoo, ISPs, disposables).
  // Runs after Turnstile so the analytics event below is bot-filtered.
  const normalizedEmail = email.trim().toLowerCase();
  if (!isBusinessEmail(normalizedEmail)) {
    // Grandfathered accounts on freemail domains fall through so
    // createPendingSignup can return "account already exists" instead.
    const existingUser = await ms_prisma.user.findFirst({
      where: {
        email: { equals: normalizedEmail, mode: "insensitive" },
        deleted: false,
      },
    });
    if (!existingUser) {
      const domain = getSignupEmailDomain(normalizedEmail);
      logger.audit("signup.blocked_freemail", {
        email: normalizedEmail,
        domain,
      });
      await trackSignupAnalytics(
        normalizedEmail,
        "signup_blocked_freemail",
        { domain },
        request,
      );
      const blockedResponse: ActionData = {
        fieldErrors: { email: BUSINESS_EMAIL_ERROR },
      };
      // If Turnstile was freshly verified, commit session to save the cache
      if (turnstileResult.sessionUpdated) {
        return Response.json(blockedResponse, {
          headers: {
            "Set-Cookie": await commitSession(session),
          },
        });
      }
      return blockedResponse;
    }
  }

  // Track signup started (normalized so analytics rows don't carry stray
  // casing/whitespace from the raw form value).
  await trackSignupAnalytics(normalizedEmail, "signup_started", null, request);
  const signupContext = {
    affiliateCode: session.get("signupAffiliate") as string | undefined,
    promoCode: session.get("signupPromo") as string | undefined,
  };

  // Pre-validate promo code so REFERRAL codes can reject existing-user emails
  // before a PendingSignup is created. The full validation runs again at
  // onboarding (without the email check, since the user exists by then).
  if (signupContext.promoCode) {
    const promoCheck = await validatePromoCode(
      signupContext.promoCode,
      normalizedEmail,
    );
    if (!promoCheck.valid) {
      return { error: promoCheck.error || "Promo code is not valid." };
    }
  }

  // Create pending signup
  const result = await createPendingSignup({
    email: normalizedEmail,
    password,
    firstName: firstName.trim(),
    lastName: lastName.trim(),
    organizationName: organizationName.trim(),
    affiliateCode: signupContext.affiliateCode,
    promoCode: signupContext.promoCode,
  });

  if (!result.success || !result.verificationCode) {
    // If Turnstile was freshly verified, commit session to save the cache
    if (turnstileResult.sessionUpdated) {
      return Response.json(
        {
          error: result.error || "Failed to create account.",
        } satisfies ActionData,
        {
          headers: {
            "Set-Cookie": await commitSession(session),
          },
        },
      );
    }
    return { error: result.error || "Failed to create account." };
  }

  // Send verification email
  const userName = `${firstName.trim()} ${lastName.trim()}`.trim();
  await sendVerificationCodeEmail(
    email.trim().toLowerCase(),
    result.verificationCode,
    userName,
  );

  // Track code sent
  await trackSignupAnalytics(email, "code_sent", null, request);

  // Store email in session for verification page
  session.set("pendingSignupEmail", email.trim().toLowerCase());

  // Clear signup context from session (will be retrieved from pending signup)
  session.unset("signupAffiliate");
  session.unset("signupPromo");

  // Redirect to verification page
  return redirect("/auth/verify-email", {
    headers: {
      "Set-Cookie": await commitSession(session),
    },
  });
};

export default function Signup() {
  const loaderData = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  // Handle case where loader returns a redirect
  if (!loaderData || "status" in loaderData) {
    return null;
  }

  const { turnstileSiteKey, turnstileRequired, inviteCodeRequired } =
    loaderData;

  return (
    <main className="flex min-h-screen items-center justify-center bg-ms-gray-50 px-4 py-12">
      {/* Signup card */}
      <div className="w-full max-w-md rounded-2xl border border-ms-gray-200 bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <img
            src="/images/mediascribe-logo.svg"
            alt="MediaScribe"
            className="mx-auto mb-6 h-10 w-auto"
          />
          <h1 className="font-serif text-2xl text-ms-gray-900">
            Create your account
          </h1>
        </div>

        {/* Error message */}
        {actionData?.error && (
          <Alert variant="error" className="mb-6">
            {actionData.error}
          </Alert>
        )}

        <Form method="post" className="space-y-5">
          {/* Invite Code (staging environments only) */}
          {inviteCodeRequired && (
            <FormInput
              type="text"
              name="inviteCode"
              label="Invite code"
              required
              placeholder="Enter your invite code"
              autoComplete="off"
            />
          )}

          {/* Name Row */}
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              type="text"
              name="firstName"
              label="First name"
              required
              autoComplete="given-name"
              error={actionData?.fieldErrors?.firstName}
            />
            <FormInput
              type="text"
              name="lastName"
              label="Last name"
              required
              autoComplete="family-name"
              error={actionData?.fieldErrors?.lastName}
            />
          </div>

          {/* Organization Name */}
          <FormInput
            type="text"
            name="organizationName"
            label="Organization name"
            required
            autoComplete="organization"
            placeholder="Your company or organization"
            error={actionData?.fieldErrors?.organizationName}
          />

          {/* Email */}
          <FormInput
            type="email"
            name="email"
            label="Business email"
            required
            autoComplete="email"
            placeholder="you@yourorganization.com"
            error={actionData?.fieldErrors?.email}
          />

          {/* Password */}
          <FormInput
            type="password"
            name="password"
            label="Password"
            required
            minLength={8}
            autoComplete="new-password"
            helperText="Must be at least 8 characters. Longer passphrases are stronger."
            error={actionData?.fieldErrors?.password}
          />

          {/* Confirm Password */}
          <FormInput
            type="password"
            name="confirmPassword"
            label="Confirm password"
            required
            minLength={8}
            autoComplete="new-password"
          />

          {/* Turnstile */}
          <TurnstileWidget
            siteKey={turnstileSiteKey || ""}
            className="mt-4"
            resetKey={actionData?.error}
            skip={!turnstileRequired}
          />

          {/* Submit */}
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText="Creating account..."
            className="w-full"
            size="md"
          >
            Continue
          </Button>
        </Form>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-ms-gray-600">
          Already have an account?{" "}
          <Link
            to="/auth/login"
            className="font-medium text-ms-primary-600 transition-colors hover:text-ms-primary-500"
          >
            Sign in to your account
          </Link>
        </p>

        <p className="mt-4 text-center text-xs text-ms-gray-500">
          By creating an account, you agree to our terms of service and privacy
          policy.
        </p>
      </div>
    </main>
  );
}
